import React, { useState, useMemo } from 'react';
import { ActivityIndicator, FlatList } from "react-native"
import type { BestAvailableOrderProps, BookStatsProps, MarketProps, OrderBookProps } from '../../types';
import { MarketComponentHelpers } from '../api';
import { Button, Text, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';

type OrderBookChartProps = {
    order_book:OrderBookProps,
    router_orders?:BestAvailableOrderProps[],
    onSelectVar1: (var_1:number) => void,
    active_var_1: number,
    market:MarketProps,
    market_type: 'FOR_MONEY'|'FREE',
    side_type?:string
    side_id?:string
    away_label?:string,
    home_label?:string
}

const OrderBookChart = ({ order_book, market, active_var_1, onSelectVar1, away_label, home_label, side_id, router_orders }:OrderBookChartProps) => {
    const Colors = useColors();
    const [ loading ] = useState(false)
    const [ expanded, setExpanded ] = useState(false);
    const [ width, setWidth ] = useState(0)

    // Merge router_orders into book_stats
    const mergedBookStats = useMemo(() => {
        if (!router_orders || router_orders.length === 0) {
            return order_book.book_stats;
        }

        // Filter router_orders by event_type, event_id, and market_id
        const filteredRouterOrders = router_orders.filter(order =>
            order.event_type == order_book.event_type &&
            order.event_id == order_book.event_id &&
            order.market_id == market.market_id
        );

        // Convert router_orders to BookStatsProps format and group by side/side_id/var_1
        const routerStatsMap = new Map<string, BookStatsProps>();

        filteredRouterOrders.forEach(order => {
            const key = `${order.side}|${order.side_id}|${order.var_1}`;
            const existing = routerStatsMap.get(key);

            if (existing) {
                // Increment order count if we already have this combination
                existing.order_count += 1;
            } else {
                // Create new BookStatsProps entry
                routerStatsMap.set(key, {
                    side: order.side,
                    side_id: order.side_id,
                    var_1: order.var_1,
                    probability: order.probability,
                    order_count: 1
                });
            }
        });

        // Merge with existing book_stats
        const mergedStatsMap = new Map<string, BookStatsProps>();

        // Add existing book_stats
        order_book.book_stats.forEach(stat => {
            const key = `${stat.side}|${stat.side_id}|${stat.var_1}`;
            mergedStatsMap.set(key, { ...stat });
        });

        // Add or merge router stats
        routerStatsMap.forEach((routerStat, key) => {
            const existing = mergedStatsMap.get(key);
            if (existing) {
                // Merge: add order counts together
                existing.order_count += routerStat.order_count;
                // Use the router probability if available (you can adjust this logic as needed)
                existing.probability = routerStat.probability;
            } else {
                // Add new stat from router_orders
                mergedStatsMap.set(key, routerStat);
            }
        });

        return Array.from(mergedStatsMap.values());
    }, [order_book.book_stats, order_book.event_type, order_book.event_id, router_orders, market.market_id])

    const renderVariableBar = (data: { item: number, index:number }) => {
        const isActive = data.item == active_var_1 ? true :  false
        let trade_side_stat = mergedBookStats.find(s => s.side === market.trade_side && s.var_1 == data.item)
        if(side_id){ trade_side_stat =  mergedBookStats.find(s => s.side === market.trade_side && s.var_1 == data.item && s.side_id == side_id)}
        let o_side_stat = mergedBookStats.find(s => {
            if(s.side !== market.trade_side && market.type === 'Spread' && s.var_1 * -1 == data.item){ return s }
            if(s.side !== market.trade_side && market.type !== 'Spread' && s.var_1 == data.item){ return s }
            return undefined
        })
        if(side_id){
            o_side_stat = mergedBookStats.find(s => {
                if(s.side_id == side_id && s.side !== market.trade_side && market.type === 'Spread' && s.var_1 * -1 == data.item){ return s }
                if(s.side_id == side_id && s.side !== market.trade_side && market.type !== 'Spread' && s.var_1 == data.item){ return s }
                return undefined
            })
        }
        let o_side_width = width * (1 - (o_side_stat?.probability ?? 1))
        let trade_side_width = width * (1 - (trade_side_stat?.probability ?? 1))
        //if(o_side_width > MAX_WIDTH){ o_side_width = MAX_WIDTH }
        //if(trade_side_width > MAX_WIDTH){ trade_side_width = MAX_WIDTH }
        return (
            <View float>
                <Button 
                    transparent 
                    style={{ padding:2, margin:2, flexDirection:'row', alignItems:'center', backgroundColor:isActive?Colors.order_book.selected:Colors.views.background }} onPress={() => onSelectVar1(data.item)}>
                    {market.var_1_required ?
                        <Text style={{ width:40 }} size={12} textAlign='center' theme='description'>{market.type === 'Spread'? data.item * -1: data.item}</Text>
                    :
                    <></>
                    }
                    <View transparent style={{flex:1, flexDirection:'row', alignItems:'center', justifyContent:'space-between'}} onLayout={(event) => {
                        if(Math.abs(event.nativeEvent.layout.width - width) < 20){ return }
                        return setWidth(event.nativeEvent.layout.width)
                    }}>
                        {o_side_stat ?
                        <View transparent style={{ width: o_side_width, height:25,  backgroundColor:Colors.text.highlight, borderRightWidth:1, borderRightColor:Colors.text.highlight,  borderTopLeftRadius:4, borderBottomLeftRadius:4, flexGrow:0 }}>
                            <View transparent style={{ position: 'absolute', paddingRight:5, top:0, right:0, left:0, bottom:0, justifyContent:'center', alignItems:'flex-end' }}>
                                <Text size={12} textAlign='right' color={Colors.text.white} theme='h2'>{MarketComponentHelpers.getOddsLabel(MarketComponentHelpers.calcAmericanOddsFromProbability(o_side_stat.probability))}</Text>
                            </View>
                        </View>
                        :
                        <View transparent style={{ flexDirection:'row', alignItems:'center', justifyContent:'space-between'}}>
                            <Text size={12} textAlign='right' style={{padding:5}} theme='description'>NA</Text>
                        </View>
                        }
                        {trade_side_stat ?
                        <View transparent style={{  width: trade_side_width, height:25, backgroundColor:Colors.text.action, borderLeftWidth:1, borderLeftColor:Colors.text.action, borderTopRightRadius:4, borderBottomRightRadius:4, flexGrow:0 }}>
                            <View transparent style={{ position: 'absolute', paddingLeft:5, top:0, right:0, left:0, bottom:0, justifyContent:'center', alignItems:'flex-start' }}>
                                <Text size={12} textAlign='right' color={Colors.text.white} theme='h1'>{MarketComponentHelpers.getOddsLabel(MarketComponentHelpers.calcAmericanOddsFromProbability(trade_side_stat.probability))}</Text>
                            </View>
                        </View>
                        :
                        <View transparent style={{ flexDirection:'row', alignItems:'center', justifyContent:'space-between'}}>
                            <Text size={12} textAlign='left' style={{padding:5}} theme='description'>NA</Text>
                        </View>
                        }
                    </View>
                    {market.var_1_required ?
                        <Text style={{ width:40 }} size={12} textAlign='center' theme='description'>{data.item}</Text>
                    :
                        <></>
                    }
                </Button>
            </View>
            
        )
    }
    if(!market || loading || !order_book){ return (
        <View style={{padding:10}}>
            <ActivityIndicator size='small' color={Colors.text.h1} />
        </View>
    ) }

    let unique_var_1 = [ ...new Set(mergedBookStats.map(s => {
        if(market.type != 'Spread'){ return s.var_1 }
        if(s.side !== market.trade_side){ return s.var_1 * -1 }
        return s.var_1
    })) ]

    let var_1_options = unique_var_1

    if(side_id){
        unique_var_1 = [ ...new Set(mergedBookStats.filter(s => s.side_id == side_id).map(s => {
        if(market.type !== 'Spread'){ return s.var_1 }
        if(s.side !== market.trade_side){ return s.var_1 * -1 }
        return s.var_1
    })) ]
    }

    if(!expanded && active_var_1){
        var_1_options = [active_var_1]
    }

    return (
        <View transparent style={{flexGrow:1, margin:20, marginTop:5 }}>
            
            <View transparent style={{flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                <Text size={14} theme='h2' textAlign='left'>{away_label?.toUpperCase()??'TBD'}</Text>
                <Text size={14} theme='h2' textAlign='center'>{market.stat_label ?? market.type?.toUpperCase()??'TBD'}</Text>
                <Text size={14} theme='h2' textAlign='right'>{home_label?.toUpperCase()??'TBD'}</Text>
            </View>
            <FlatList data={var_1_options.sort((a,b) => a - b)} renderItem={renderVariableBar} keyExtractor={(item) => item.toString()} extraData={width} />
            {market.var_1_required ?
            <Button transparent style={{ padding:10 }} onPress={() => setExpanded(!expanded)}>
                {!expanded ?
                <Text size={12} color={Colors.text.action} weight='semibold' textAlign='center'>{unique_var_1.length} Other {market.type}s Available</Text>
                :
                <Text size={12} color={Colors.text.error} weight='semibold' textAlign='center'>Hide Alternatives</Text>
                }
            </Button>
            :<></>}
            {unique_var_1.length == 0 ?
            <Text size={12} textAlign='center' style={{padding:8}} theme='description'>No Active Orders</Text> 
            :<></>}
        </View>
    )
}

export default OrderBookChart